import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { FieldChipList, resultFieldEntries } from '@/components/fields/FieldChipList'
import {
  FxAlert,
  FxButton,
  FxIcon,
  FxPanel,
  FxSegmentedTabs,
  FxSpinner,
  FxTextField,
} from '@/components/fx'
import { isPdfName } from '@/components/upload'
import { PageViewer, type ViewerBox } from '@/components/viewer/PageViewer'
import { FunnelLayout } from '@/layouts/FunnelLayout'
import {
  ApiError,
  asLimitRefusal,
  billingApi,
  documentsApi,
  exportErrorMessage,
  exportsApi,
  readBlobError,
  templatesApi,
} from '@/lib/api'
import { useUsage } from '@/hooks/useUsage'
import { cn } from '@/lib/utils'
import type { EvaluationDocument } from '@/types/domain'

/**
 * How long an analysis may sit unfinished before the screen stops promising
 * it is nearly there.
 *
 * A page takes seconds; a minute and a half means the job is stuck behind
 * something, and at that point the useful thing is not a nicer spinner but a
 * way out. Polling continues either way — a job that finishes late still
 * lands on screen by itself.
 */
const STALL_AFTER_MS = 90_000

/**
 * How long a freshly uploaded PDF is given to become pages, and how often we
 * look.
 *
 * The converter records a failed conversion on the *job*, so a PDF it cannot
 * render leaves nothing a client can read — no pages, no marker. A wait with
 * no deadline would therefore be a wait with no end.
 */
const CONVERT_DEADLINE_MS = 2 * 60_000
const CONVERT_POLL_MS = 2000

/**
 * The ids of the documents a PDF upload turned into, or an empty list if it
 * has not turned into any within the deadline.
 *
 * `GET …/documents?originalDocumentId=` matches on `original_document_id`,
 * which the converter writes on every rendered page, so the placeholder id
 * stays a usable handle on the upload even after its own row is deleted.
 */
async function convertedPageIds(templateId: string, placeholderId: string): Promise<string[]> {
  const until = Date.now() + CONVERT_DEADLINE_MS
  for (;;) {
    const pages = await documentsApi.pages(templateId, placeholderId)
    const converted = pages.filter((page) => page.id !== placeholderId)
    if (converted.length > 0) return converted.map((page) => page.id)
    if (Date.now() >= until) return []
    await new Promise((resolve) => window.setTimeout(resolve, CONVERT_POLL_MS))
  }
}

export type ResultBranch =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'missing'; stalled: boolean }
  | { kind: 'pending'; stalled: boolean }
  | { kind: 'failed'; message: string }
  | { kind: 'noMatch'; reason: string }
  | { kind: 'noFields' }
  | { kind: 'ready' }

/**
 * What this screen is actually looking at.
 *
 * Every outcome gets a name so the render is a lookup rather than a chain of
 * conditions, and so the three failure branches drawn on DemoStates are
 * reachable in a test without a server.
 *
 * `'failed'` is the status the worker writes on an analysis failure
 * (`persist.py`, `engine.py`); `'problematic'` is the older word for it and
 * the only one `EvaluationStatus` in `types/domain.ts` knows, so both are
 * accepted here.
 */
export function resultBranch(input: {
  loading: boolean
  error?: unknown
  doc?: EvaluationDocument
  fieldCount: number
  stalled: boolean
}): ResultBranch {
  if (input.loading) return { kind: 'loading' }
  // A failed request leaves no document, which would otherwise read as "the
  // analysis has not landed yet" — a spinner that never polls again, because
  // the poll is driven by the very list that failed to arrive.
  if (input.error) {
    return { kind: 'error', message: ApiError.from(input.error).message }
  }

  const doc = input.doc
  if (!doc) return { kind: 'missing', stalled: input.stalled }

  const status: string = doc.evaluationStatus
  if (status === 'noMatch') {
    return {
      kind: 'noMatch',
      reason: doc.noMatchReason ?? 'Wir konnten die Seite dem Formular nicht zuordnen.',
    }
  }
  if (status === 'failed' || status === 'problematic') {
    return { kind: 'failed', message: doc.error ?? 'Die Auswertung ist fehlgeschlagen.' }
  }
  if (status === 'neu' || status === 'inProgress') {
    return { kind: 'pending', stalled: input.stalled }
  }
  // A finished analysis with nothing in it means the template carries no
  // boxes — the form was stored, the detector found nothing to read.
  if (input.fieldCount === 0) return { kind: 'noFields' }
  return { kind: 'ready' }
}

/**
 * Which stored image to show, and whether the field boxes belong on it.
 *
 * The worker stores three pictures of one page under different keys, and only
 * two of them share the frame a result field's coordinates are written in:
 * `enhancedImagePath` and `evalImagePath` are the scan warped onto the
 * template, `imagePath` is the upload as it arrived — photographed at an
 * angle, at its own resolution, never straightened. Drawing the boxes on that
 * one puts them somewhere arbitrary, which reads as a recognition failure
 * rather than as the unit error it is; the Flutter viewer makes the same call
 * in `overlayFitsSource`.
 *
 * The enhanced page comes first because the analysed one already carries the
 * worker's own rectangles, and this screen draws its own over it.
 */
export function pageImage(doc: EvaluationDocument | undefined): {
  path: string
  boxesFit: boolean
} {
  const framed = doc?.enhancedImagePath || doc?.evalImagePath
  if (framed) return { path: framed, boxesFit: true }
  return { path: doc?.imagePath ?? '', boxesFit: false }
}

/** Whether the list of analysed pages is worth polling for again. */
function stillRunning(docs: EvaluationDocument[] | undefined): boolean {
  if (!docs) return false
  if (docs.length === 0) return true
  return docs.some(
    (doc) => doc.evaluationStatus === 'neu' || doc.evaluationStatus === 'inProgress',
  )
}

type Filter = 'unclear' | 'all'

type Note = { tone: 'info' | 'warning' | 'danger'; title?: string; text: string } | null

/**
 * Step four of the funnel: the scan, read.
 *
 * Everything before this has been a promise. This is the screen where someone
 * sees their own handwriting come back as text, so it shows the page and the
 * readings side by side, says plainly which ones it is unsure about, and
 * hands over the file.
 */
export function ResultPage() {
  const { templateId = '', documentId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Null until the person chooses: the list opens on the flagged fields when
  // there are any, and on all of them when the scan read cleanly — otherwise
  // the best possible outcome of the funnel presents as an empty box headed
  // "Unklar (0)".
  const [filter, setFilter] = useState<Filter | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pageIndex, setPageIndex] = useState(0)
  const [busy, setBusy] = useState<'csv' | 'xlsx' | 'upload' | null>(null)
  const [note, setNote] = useState<Note>(null)
  const [stalled, setStalled] = useState(false)

  const template = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => templatesApi.get(templateId),
  })

  const pages = useQuery({
    queryKey: ['document-pages', templateId, documentId],
    queryFn: () => documentsApi.pages(templateId, documentId),
    // The funnel lands here the moment the job reports done, but a person who
    // reloads or comes back from the phone can arrive mid-analysis.
    refetchInterval: (query) => (stillRunning(query.state.data) ? 3000 : false),
  })

  const docs = pages.data ?? []
  const doc = docs[Math.min(pageIndex, Math.max(0, docs.length - 1))]
  const threshold = template.data?.confidenceThreshold ?? 0.8

  const entries = useMemo(
    () => resultFieldEntries(doc, template.data, threshold),
    [doc, template.data, threshold],
  )
  const unclear = useMemo(() => entries.filter((entry) => entry.flagged), [entries])

  const waiting = stillRunning(pages.data)
  useEffect(() => {
    if (!waiting) {
      setStalled(false)
      return
    }
    const timer = window.setTimeout(() => setStalled(true), STALL_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [waiting])

  const branch = resultBranch({
    loading: pages.isPending || template.isPending,
    error: pages.error ?? template.error,
    doc,
    fieldCount: entries.length,
    stalled,
  })

  const boxes: ViewerBox[] = useMemo(
    () =>
      entries
        .filter((entry) => entry.rect != null)
        .map((entry) => ({
          id: entry.id,
          label: entry.name,
          rect: entry.rect!,
          tone: entry.tone,
        })),
    [entries],
  )

  // "Weitere Dokumente hochladen" lands on the same route with another id, so
  // React Router keeps this component — and with it a page tab, a selection
  // and a filter that belong to the previous scan.
  useEffect(() => {
    setPageIndex(0)
    setSelectedId(null)
    setFilter(null)
  }, [documentId])

  const effectiveFilter: Filter = filter ?? (unclear.length > 0 ? 'unclear' : 'all')
  const shown = effectiveFilter === 'unclear' ? unclear : entries
  const shownPage = pageImage(doc)

  async function download(format: 'csv' | 'xlsx') {
    setBusy(format)
    setNote(null)
    try {
      const outcome = await exportsApi.download(templateId, format, {
        method: 'specific',
        // The ids of the analysed pages, which is what the results endpoint
        // selects on; the route's `documentId` is the uploaded file and is
        // only the same id when the upload was a single image.
        documentIds: docs.map((entry) => entry.id),
        // The whole point of this screen is which readings to check, so the
        // file carries the confidence column with it.
        includeConfidence: true,
      })
      if (outcome === 'newTab') {
        setNote({
          tone: 'info',
          text: 'Die Datei wurde in einem neuen Tab geöffnet — von dort kannst du sie sichern.',
        })
      } else if (outcome === 'blocked') {
        setNote({
          tone: 'danger',
          text: 'Der Browser hat den Download blockiert. Bitte erlaube Pop-ups für diese Seite.',
        })
      }
    } catch (cause) {
      // A failed binary request carries its reason inside the blob, not in
      // the axios message.
      const error = await readBlobError(cause)
      setNote({ tone: 'danger', title: 'Export nicht möglich', text: exportErrorMessage(error) })
    } finally {
      setBusy(null)
    }
  }

  async function uploadMore(files: FileList | null) {
    if (!files || files.length === 0) return
    setBusy('upload')
    setNote(null)
    try {
      const entries = Array.from(files).map((file) => ({ file, filename: file.name }))
      const ids = await documentsApi.uploadAll(templateId, entries)
      void queryClient.invalidateQueries({ queryKey: ['usage'] })

      // A PDF's id is a placeholder the converter deletes once it has
      // rendered the pages (`convertDocument`): it writes one document per
      // page and removes the row the upload answered with. An analysis
      // started on that id would be a job on a document that no longer
      // exists, and this screen would poll pages that never leave `neu`. So
      // a PDF is waited out first and analysed under the ids that exist.
      const uploads = ids.map((id, index) => ({
        id,
        isPdf: isPdfName(entries[index].filename),
      }))
      if (uploads.some((upload) => upload.isPdf)) {
        setNote({
          tone: 'info',
          text: 'Dein PDF wird in einzelne Seiten umgewandelt — das dauert einen Moment.',
        })
      }
      const readable: { handle: string; pageIds: string[] }[] = []
      for (const upload of uploads) {
        const pageIds = upload.isPdf
          ? await convertedPageIds(templateId, upload.id)
          : [upload.id]
        if (pageIds.length > 0) readable.push({ handle: upload.id, pageIds })
      }

      const analysable = readable.flatMap((entry) => entry.pageIds)
      if (analysable.length === 0) {
        // Every uploaded file was a PDF and none of them became pages in
        // time. The files are stored either way, so this is a "look later",
        // not a loss — and the cockpit is where they will appear.
        setNote({
          tone: 'warning',
          title: 'Das Umwandeln dauert länger als erwartet',
          text: 'Deine PDF-Seiten sind noch nicht fertig. Sie erscheinen in deinem Formular, sobald sie da sind — dort kannst du sie auswerten lassen.',
        })
        return
      }
      setNote(null)
      await documentsApi.startAnalysis(templateId, analysable)
      // The screen follows the *upload* id, not a page id: it asks
      // `…?originalDocumentId=`, which answers with every page of that
      // upload, so a multi-page PDF arrives complete rather than as page one.
      navigate(`/onboarding/ergebnis/${templateId}/${readable[0].handle}`)
    } catch (cause) {
      const error = ApiError.from(cause)
      const refusal = asLimitRefusal(error)
      if (refusal) {
        setNote({
          tone: 'warning',
          title: 'Dein Kontingent ist aufgebraucht',
          text: `${refusal.used} von ${refusal.limit} Seiten verbraucht. Deine Formulare und Ergebnisse bleiben erhalten — ansehen und exportieren geht weiter.`,
        })
      } else {
        setNote({ tone: 'danger', title: 'Das hat nicht funktioniert', text: error.message })
      }
    } finally {
      setBusy(null)
    }
  }

  return (
    <FunnelLayout
      step="result"
      right={
        <Link to="/" className="whitespace-nowrap font-semibold text-primary-text">
          Zu meinen Formularen →
        </Link>
      }
    >
      <main className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-16 md:py-8">
        {branch.kind === 'loading' ? (
          <div className="grid place-items-center py-16">
            <FxSpinner label="Ergebnis wird geladen …" />
          </div>
        ) : branch.kind === 'ready' ? (
          <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
            <section className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <h1 className="m-0 flex-1 text-dialog-title">
                  {entries.length} {entries.length === 1 ? 'Feld' : 'Felder'} gelesen
                </h1>
                <span className="inline-flex items-center gap-2 text-small text-success-text">
                  <FxIcon name="check_circle" size={20} />
                  {entries.length - unclear.length} sicher
                </span>
                {unclear.length > 0 && (
                  <span className="inline-flex items-center gap-2 text-small text-danger-text">
                    <FxIcon name="error" size={20} />
                    {unclear.length} unklar
                  </span>
                )}
              </div>

              {docs.length > 1 && (
                <FxSegmentedTabs
                  aria-label="Seite"
                  className="flex-nowrap gap-2 [&>button]:w-auto [&>button]:flex-1"
                  items={docs.map((entry, index) => ({
                    value: String(index),
                    label: `Seite ${entry.page ?? index + 1}`,
                  }))}
                  active={String(pageIndex)}
                  onSelect={(value) => {
                    setPageIndex(Number(value))
                    setSelectedId(null)
                  }}
                />
              )}

              <PageViewer
                imagePath={shownPage.path}
                // The enhanced and analysed pages are the scan warped into
                // the template's frame, which is the frame a result field's
                // `x/y/x2/y2` is in — so the boxes are the image's own pixels.
                frame="image"
                boxes={shownPage.boxesFit ? boxes : []}
                selectedBoxId={selectedId}
                onSelectBox={setSelectedId}
                alt={`Ausgewertete Seite von „${template.data?.title ?? 'deinem Formular'}“`}
              />

              {shownPage.boxesFit ? (
                <Legend />
              ) : (
                <p className="text-small text-text-muted">
                  Für diese Seite liegt kein ausgerichtetes Seitenbild vor — die Felder lassen sich
                  darauf nicht einzeichnen. Die gelesenen Werte stehen daneben.
                </p>
              )}
            </section>

            <aside className="flex flex-col gap-5">
              <FxPanel className="flex flex-col gap-3 p-5">
                <FxSegmentedTabs
                  aria-label="Felder filtern"
                  className="flex-nowrap gap-2 [&>button]:w-auto [&>button]:flex-1"
                  items={[
                    { value: 'unclear', label: `Unklar (${unclear.length})` },
                    { value: 'all', label: `Alle (${entries.length})` },
                  ]}
                  active={effectiveFilter}
                  onSelect={(value) => setFilter(value as Filter)}
                />

                <FieldChipList
                  label={effectiveFilter === 'unclear' ? 'Unklare Felder' : 'Gelesene Felder'}
                  className="max-h-[380px] overflow-y-auto"
                  fields={shown}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  emptyText={
                    effectiveFilter === 'unclear'
                      ? 'Kein Feld ist unklar — alle Werte liegen über deiner Sicherheitsschwelle.'
                      : 'Für diese Seite liegen keine Werte vor.'
                  }
                />

                <div className="grid grid-cols-2 gap-3">
                  <FxButton
                    variant="successTonal"
                    icon="download"
                    disabled={busy != null}
                    onClick={() => void download('xlsx')}
                  >
                    Excel
                  </FxButton>
                  <FxButton
                    variant="tonal"
                    icon="download"
                    disabled={busy != null}
                    onClick={() => void download('csv')}
                  >
                    CSV
                  </FxButton>
                </div>

                {note && (
                  <FxAlert tone={note.tone} title={note.title}>
                    {note.text}
                  </FxAlert>
                )}
              </FxPanel>

              <StackPanel />

              {/* The phone gets these two as a bar pinned to the bottom. */}
              <div className="hidden flex-col gap-3 lg:flex">
                <UploadMoreButton busy={busy === 'upload'} onFiles={uploadMore} />
                <FxButton variant="tonal" asChild>
                  <Link to={`/templates/${templateId}`}>Felder anpassen</Link>
                </FxButton>
              </div>

              {/* Cut on the phone, as DemoPhone draws it: the small screen
                  gets the result and one next step. */}
              <p className="hidden items-start gap-3 text-small lg:flex">
                <FxIcon name="cloud_done" size={20} className="shrink-0 text-success-text" />
                <span>
                  Formular und Ergebnis sind in deinem Konto gespeichert.{' '}
                  <Link className="font-semibold text-primary-text" to={`/templates/${templateId}`}>
                    Zum Formular →
                  </Link>
                </span>
              </p>
            </aside>

            <MobileActionBar busy={busy} onFiles={uploadMore} onExcel={() => void download('xlsx')} />
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[720px]">
            <Branch
              branch={branch}
              templateId={templateId}
              busy={busy === 'upload'}
              onFiles={uploadMore}
              onRetry={() => void pages.refetch()}
              onReanalyse={async () => {
                setNote(null)
                try {
                  await documentsApi.startAnalysis(templateId, [doc?.id ?? documentId])
                  void pages.refetch()
                } catch (cause) {
                  setNote({
                    tone: 'danger',
                    title: 'Das hat nicht funktioniert',
                    text: ApiError.from(cause).message,
                  })
                }
              }}
              note={note}
            />
          </div>
        )}
      </main>
    </FunnelLayout>
  )
}

function Legend() {
  const items = [
    { label: 'sicher', className: 'border-success' },
    { label: 'knapp', className: 'border-warning' },
    { label: 'unklar – bitte prüfen', className: 'border-danger bg-danger/20' },
  ]
  return (
    <ul className="flex flex-wrap gap-4 text-small">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-2">
          <span className={cn('h-3.5 w-3.5 border-[3px]', item.className)} aria-hidden="true" />
          {item.label}
        </li>
      ))}
    </ul>
  )
}

function UploadMoreButton({
  busy,
  onFiles,
  variant = 'primary',
}: {
  busy: boolean
  onFiles: (files: FileList | null) => void | Promise<void>
  variant?: 'primary' | 'tonal'
}) {
  // A label with a hidden input rather than a button that clicks an input:
  // the label is the input's accessible name, so the control is one thing to
  // a screen reader and to the keyboard. The focus ring has to be drawn on
  // the label, though — the focused element is the `sr-only` input, clipped
  // to a pixel, so its own outline is invisible.
  return (
    <FxButton variant={variant} block asChild>
      <label
        className={cn(
          'cursor-pointer',
          'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary-strong',
          busy && 'pointer-events-none opacity-60',
        )}
      >
        <FxIcon name="upload" size={22} />
        {busy ? 'Wird hochgeladen …' : 'Weitere Dokumente hochladen'}
        <input
          type="file"
          className="sr-only"
          accept=".pdf,.png,.jpg,.jpeg"
          multiple
          // On a phone this opens the camera directly, which is how the
          // funnel's scans are taken.
          capture="environment"
          disabled={busy}
          onChange={(event) => {
            void onFiles(event.target.files)
            event.target.value = ''
          }}
        />
      </label>
    </FxButton>
  )
}

/**
 * The phone's two actions, pinned to the bottom.
 *
 * The bar respects the home-indicator inset; without that the second button
 * sits under the gesture bar on an iPhone and cannot be tapped.
 */
function MobileActionBar({
  busy,
  onFiles,
  onExcel,
}: {
  busy: string | null
  onFiles: (files: FileList | null) => void | Promise<void>
  onExcel: () => void
}) {
  return (
    <div
      className="sticky bottom-0 -mx-4 flex flex-col gap-3 bg-surface px-4 pt-4 shadow-[0_-1px_0_hsl(var(--fx-divider))] lg:hidden"
      style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <UploadMoreButton busy={busy === 'upload'} onFiles={onFiles} />
      <FxButton variant="successTonal" block icon="download" disabled={busy != null} onClick={onExcel}>
        Als Excel herunterladen
      </FxButton>
    </div>
  )
}

/**
 * "Und dein ganzer Stapel?" — what a batch of this size would consume.
 *
 * The artboard quotes euro amounts and a recommended package. The gateway
 * supplies neither: `GET /v1/billing/packages` returns `{key, priceId}` with
 * the price living in Stripe, and no figure in the design exists in config or
 * code. What it does return is `rates` — credits per page per operation — so
 * that is what this panel says, and it says nothing at all when the rates are
 * missing. A made-up price beside a "Jetzt kaufen" is the one number nobody
 * may see.
 */
function StackPanel() {
  const [pagesWanted, setPagesWanted] = useState('500')
  const [operation, setOperation] = useState<'standard' | 'praezision'>('standard')
  const usage = useUsage()
  const packages = useQuery({
    queryKey: ['billing-packages'],
    queryFn: () => billingApi.packages(),
    // Prices and rates do not move during a session.
    staleTime: 5 * 60_000,
    retry: false,
  })

  const rates = packages.data?.rates
  const count = Number.parseInt(pagesWanted, 10)
  const known = rates?.standard != null && rates?.praezision != null
  if (!known) return null

  const credits = Number.isFinite(count) && count > 0 ? count * (rates?.[operation] ?? 0) : null
  const free = usage.data?.enforced ? usage.data.usage?.pagesRemaining : undefined

  return (
    <FxPanel className="hidden flex-col gap-4 p-6 ring-2 ring-inset ring-primary-tonal lg:flex">
      <h2 className="m-0 text-panel-title">Und dein ganzer Stapel?</h2>

      <FxTextField
        label="Wie viele solcher Formulare hast du?"
        inputMode="numeric"
        value={pagesWanted}
        onChange={(event) => setPagesWanted(event.target.value)}
        containerClassName="max-w-[200px]"
        trailing={<span className="text-body text-text-muted">Seiten</span>}
      />

      <fieldset className="m-0 grid grid-cols-2 gap-3 border-0 p-0">
        <legend className="sr-only">Wie sorgfältig gelesen werden soll</legend>
        {(
          [
            // Not "wie dieses Ergebnis" any more. The trial is read by the
            // model — that is the point of it — so pointing at this result as
            // an example of the cheaper tier would undersell what was just
            // shown and oversell what Standard delivers.
            { value: 'standard', title: 'Standard', hint: 'Texterkennung, für klare Vorlagen' },
            { value: 'praezision', title: 'Präzision', hint: 'wie dieses Ergebnis: zusätzlich von einem KI-Modell gelesen' },
          ] as const
        ).map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex cursor-pointer flex-col gap-1 rounded-sm p-3',
              operation === option.value
                ? 'bg-primary-subtle ring-2 ring-inset ring-primary'
                : 'bg-ground',
            )}
          >
            <span className="flex items-center gap-2 text-body-strong">
              <input
                type="radio"
                name="operation"
                className="accent-[hsl(var(--fx-primary))]"
                checked={operation === option.value}
                onChange={() => setOperation(option.value)}
              />
              {option.title}
            </span>
            <span className="text-body-strong tabular-nums">
              {(rates?.[option.value] ?? 0).toLocaleString('de-DE')} Guthaben je Seite
            </span>
            <span className="text-caption text-text-muted">{option.hint}</span>
          </label>
        ))}
      </fieldset>

      {credits != null && (
        <p className="m-0 text-body">
          {count.toLocaleString('de-DE')} Seiten brauchen{' '}
          <b className="tabular-nums">{credits.toLocaleString('de-DE')} Guthaben</b>.
        </p>
      )}

      {/* Not "Startguthaben": that is the credit ledger's word, and this
          figure is the plan's page allowance — a different currency in the
          same panel, where the line above is quoted in credits. */}
      {free != null && (
        <p className="m-0 text-small text-text-muted">
          In deinem Tarif sind noch {free} Seiten frei.
        </p>
      )}
    </FxPanel>
  )
}

/** The outcomes that are not a result: the three DemoStates panels and the
 *  two the sheet does not draw (a failed job, a document with no analysis). */
function Branch({
  branch,
  templateId,
  busy,
  onFiles,
  onRetry,
  onReanalyse,
  note,
}: {
  branch: ResultBranch
  templateId: string
  busy: boolean
  onFiles: (files: FileList | null) => void | Promise<void>
  onRetry: () => void
  onReanalyse: () => void | Promise<void>
  note: Note
}) {
  const toForm = (
    <FxButton variant="tonal" asChild>
      <Link to={`/templates/${templateId}`}>Zum Formular</Link>
    </FxButton>
  )

  if (branch.kind === 'noMatch') {
    // DemoStates draws a second way out here, "Ecken selbst setzen", leading
    // to the Ausrichten dialog: the gateway takes `manual_corners` (four
    // fractions, TL/TR/BR/BL) on a job and the worker builds the transform
    // from them, which is exactly the case a mis-aligned photo is. That
    // dialog is not in this wave, so the button is left out rather than
    // wired to nothing — when it lands, it belongs beside this upload.
    return (
      <StateCard
        tone="warning"
        icon="crop_rotate"
        title="Der Scan passt nicht zum Formular"
        body={`${branch.reason} Bei einem Foto hilft meist: gerade von oben, das ganze Blatt im Bild, gleichmäßiges Licht.`}
        note={note}
      >
        <UploadMoreButton busy={busy} onFiles={onFiles} />
        {toForm}
      </StateCard>
    )
  }

  if (branch.kind === 'noFields') {
    return (
      <StateCard
        tone="warning"
        icon="search_off"
        title="Es wurden keine Felder gefunden"
        body="Bei diesem Formular hat die Erkennung nichts gefunden. Das Formular ist gespeichert — die Felder kannst du im Editor selbst einzeichnen."
        note={note}
      >
        <FxButton asChild>
          <Link to={`/templates/${templateId}`}>Felder einzeichnen</Link>
        </FxButton>
        <UploadMoreButton busy={busy} onFiles={onFiles} variant="tonal" />
      </StateCard>
    )
  }

  if (branch.kind === 'error') {
    return (
      <StateCard
        tone="danger"
        icon="cloud_off"
        title="Dein Ergebnis lässt sich gerade nicht laden"
        body={`${branch.message} Dein Scan ist gespeichert — versuch es gleich noch einmal.`}
        note={note}
      >
        <FxButton onClick={onRetry}>Noch einmal laden</FxButton>
        {toForm}
      </StateCard>
    )
  }

  if (branch.kind === 'failed') {
    return (
      <StateCard
        tone="danger"
        icon="error"
        title="Die Auswertung ist fehlgeschlagen"
        body={branch.message}
        note={note}
      >
        <FxButton onClick={() => void onReanalyse()}>Noch einmal auswerten</FxButton>
        {toForm}
      </StateCard>
    )
  }

  // Still running, or running for so long that saying so is no longer honest.
  const stalled = branch.kind === 'missing' ? branch.stalled : branch.kind === 'pending' ? branch.stalled : false
  if (!stalled) {
    return (
      <FxPanel title="Dein Scan wird ausgewertet">
        <p className="text-body text-text-muted">
          Wir richten die Seite am Formular aus und lesen die Felder. Das dauert einen Moment —
          diese Seite aktualisiert sich von selbst.
        </p>
        <FxSpinner className="mt-4" label="Auswertung läuft …" />
      </FxPanel>
    )
  }

  return (
    <StateCard
      tone="warning"
      icon="hourglass_disabled"
      title="Die Auswertung dauert ungewöhnlich lange"
      body="Der Auftrag läuft noch oder ist irgendwo hängen geblieben. Dein Scan ist gespeichert — du kannst die Auswertung neu starten oder es später wieder versuchen."
      note={note}
    >
      <FxButton onClick={() => void onReanalyse()}>Auswertung neu starten</FxButton>
      <FxButton variant="tonal" onClick={onRetry}>
        Jetzt nachsehen
      </FxButton>
    </StateCard>
  )
}

/** A DemoStates panel: a tinted header strip, a sentence, and a way out. */
function StateCard({
  tone,
  icon,
  title,
  body,
  note,
  children,
}: {
  tone: 'warning' | 'danger'
  icon: string
  title: string
  body: string
  note: Note
  children: React.ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-sm bg-surface shadow-raised">
      <div
        className={cn(
          'flex items-start gap-4 p-7',
          tone === 'danger' ? 'bg-danger-subtle' : 'bg-warning-subtle',
        )}
      >
        <FxIcon
          name={icon}
          size={32}
          className={tone === 'danger' ? 'text-danger-text' : 'text-warning-text'}
        />
        <h1 className="m-0 text-panel-title">{title}</h1>
      </div>
      <div className="flex flex-col gap-4 p-7">
        <p className="m-0 text-body">{body}</p>
        {note && (
          <FxAlert tone={note.tone} title={note.title}>
            {note.text}
          </FxAlert>
        )}
        <div className="flex flex-col gap-3 sm:flex-row">{children}</div>
      </div>
    </section>
  )
}
